import { ref } from 'vue'
import { defineStore } from 'pinia'
import { searchPatient } from '@/services/api/fhirAPI/patient/patient.ts'
import { useSelectedPlanStore } from '@/stores/plans/selectedPlan.ts'
import { useCoverageStore } from '@/stores/coverage/coverage.ts'
import { useAccessRightsStore } from '@/stores/accessRights/accessRights'
import { toPascalCase } from '@/utils/utils.js'

export const usePatientStore = defineStore(
	'patientData',
	() => {
		// ====---------------====
		// State
		// ====---------------====
		const planStore = useSelectedPlanStore().getStore()

		const state = ref<PatientData>({})
		const loaded = ref(false)

		// ====---------------====
		// Getters
		// ====---------------====
		async function loadStore(): Promise<PatientData | null> {
			if (!loaded.value) await loadPatientData()

			return state.value
		}

		function getMemberName(memberId: string): FhirName | null {
			return state.value?.[memberId]?.name?.[0] ?? null
		}

		function fullName(nameList: FhirName[] | undefined): string {
			if (nameList?.[0]?.given?.[0])
				return toPascalCase(`${nameList[0].given[0]} ${nameList[0].family}`)
			else return ''
		}

		// Helper function to extract member name
		function getMemberNameString(memberId: string, nameType: 'first' | 'full'): string {
			const names = state.value?.[memberId]?.name

			switch (nameType) {
				case 'full':
					return fullName(names)
				default:
					return names?.[0]?.given?.[0] || ''
			}
		}

		function checkFuture(memberId: string): boolean {
			const coveredIds = useCoverageStore().getRecentCoverage()
			if (Object.keys(coveredIds).length > 0) {
				const coverageData: FhirCoverage | null = coveredIds[memberId] ?? null
				if (coverageData && coverageData?.status === 'active') {
					return true
				}
				return false
			} else {
				return true
			}
		}

		function getFullMemberList(
			nameType: 'first' | 'full' = 'first',
			activeOnly: boolean = false,
			allowAllFuture: boolean = false
		): SelectItem[] {
			const items: SelectItem[] = []
			const coveredIds = Object.keys(useCoverageStore().getRecentCoverage())

			if (!state.value) return items

			Object.values(state.value).forEach((member) => {
				const memberId = member.id
				const memberName = getMemberNameString(memberId, nameType)

				// Skip if no name available
				if (!memberName) return

				// Determine if member should be included
				const isCovered = coveredIds.includes(memberId)
				const meetsActiveFilter = !activeOnly || member.active
				const meetsFutureFilter = allowAllFuture && checkFuture(memberId)
				const shouldInclude = meetsFutureFilter || (meetsActiveFilter && isCovered)

				if (shouldInclude) {
					items.push({
						title: memberName,
						value: memberId
					})
				}
			})

			return items
		}

		function getAccessibleMemberList(): SelectItem[] | null {
			let items: SelectItem[] = []

			useAccessRightsStore().state?.accessibleMembers.forEach((member) => {
				if (state.value && state.value[member.memberId]) {
					const memberName = state.value?.[member.memberId]?.name
						? fullName(state.value[member.memberId]?.name)
						: ''

					items.push({
						title: memberName,
						value: member.memberId
					})
				}
			})

			return items.length > 0 ? items : null
		}

		// ====---------------====
		// Actions
		// ====---------------====
		function updatePatientData(memberId: string, payload: FhirResourceBase) {
			state.value[memberId] = payload
			loaded.value = true
		}

		function $reset() {
			state.value = {}
			loaded.value = false
		}

		function resetPatientData() {
			$reset()
		}

		// fetch patient data
		async function fetchPatient() {
			const requestId = planStore.isPolicyHolder ? planStore.contractId : planStore.memberId
			return await searchPatient(requestId)
				.then((res: FhirBundle<FhirPatient>) => {
					if (
						res.entry &&
						res.entry.length === 1 &&
						res.entry[0].resource?.issue?.code == 'exception'
					)
						throw new Error()
					res.entry?.forEach((member) => {
						if (member.resource.id) updatePatientData(member.resource.id, member.resource)
					})
				})
				.catch((err: string) => {
					console.error(err)
				})
		}

		// this isn't needed for the patient store, but there are some cases where both are needed (spending details for one)
		async function fetchCoverage(): Promise<string> {
			if (useCoverageStore().loaded) return ''

			try {
				return await useCoverageStore().loadCoverageStore()
			} catch (error) {
				throw error
			}
		}

		let loadPatientRequest: Promise<string> | null = null
		async function loadPatientData() {
			if (loadPatientRequest) {
				return loadPatientRequest
			}

			loadPatientRequest = new Promise((resolve, reject) => {
				Promise.allSettled([fetchPatient(), fetchCoverage()])
					.then(() => {
						resolve('')
					})
					.catch((err) => {
						reject(err)
					})
					.finally(() => {
						loadPatientRequest = null
					})
			})
			return loadPatientRequest
		}

		// ====---------------====
		// Return
		// ====---------------====
		return {
			state,
			loaded,
			getMemberName,
			getFullMemberList,
			getMemberNameString,
			getAccessibleMemberList,
			loadPatientData,
			loadStore,
			updatePatientData,
			resetPatientData,
			$reset
		}
	},
	{
		persist: {
			storage: sessionStorage
		}
	}
)
